import { memo, useEffect, useMemo, useState } from 'react'
import { Autocomplete, Grid, TextField, createFilterOptions } from '@mui/material'
import Section from 'components/common/Section/Section'
import ProductInfoItem from '../ProductInfoItem/ProductInfoItem'
import { colors } from 'styles/colors'
import { useProductContext } from 'context/ProductContext'
import PencilIcon from 'icons/PencilIcon'
import SaveIcon from 'icons/SaveIcon'
import XIcon from 'icons/XIcon'
import { useUpdateProductBrand } from 'hooks/useProduct'
import { useGetBrands } from 'hooks/useRawProductCategoryReview'
import { BrandOption } from 'services/rawProductService'
import { snackbarService } from 'services/snackbarService'

// Searched on every spelling a brand has been seen as, not only its two names:
// a reviewer types what the pack prints.
const filterBrands = createFilterOptions<BrandOption>({
  stringify: (brand) => [brand.nameEn, brand.nameAr, ...brand.aliases].filter(Boolean).join(' '),
});

// Which brand this product is sold under. Only the choice is made here: the
// brand's names and the spellings packs print it as are written on the raw
// product's review card, where the pack is in front of the reviewer.
function BrandDetails() {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();
  const { data: brands } = useGetBrands();
  const [selected, setSelected] = useState<BrandOption | null>(null);

  // A brand created since the list was fetched is not in it, so the product's
  // own brand is always an option - otherwise the picker opens empty on the very
  // brand somebody means to change.
  const options: BrandOption[] = useMemo(() => {
    const list: BrandOption[] = brands ?? [];
    const current = product.brand;
    if (!current || list.some((brand) => brand.id === current.id)) return list;
    return [{ ...current, productCount: 0 }, ...list];
  }, [brands, product.brand]);

  const brandId = product.brand?.id ?? null;
  useEffect(() => {
    setSelected(options.find((brand: BrandOption) => brand.id === brandId) ?? null);
  }, [options, brandId, isEditing]);

  const { mutate: updateMutation, isLoading } = useUpdateProductBrand(
    () => {
      setIsEditing(false);
      snackbarService.showSnackbar('Product brand has been updated successfully', 'success');
      refetchProduct();
    },
    () => { },
  )

  return (
    <Section
      title={'Brand & companies'}
      buttons={[
        ...(isEditing
          ? [
            {
              icon: <SaveIcon size={20} color={colors.success} />,
              title: 'Save',
              onClick: () => updateMutation({ id: product.id, brandId: selected?.id ?? null }),
              loading: isLoading,
            },
            {
              icon: <XIcon size={15} color={colors.error} />,
              title: 'Cancel',
              onClick: () => setIsEditing(false),
              disabled: isLoading,
            },
          ]
          : [
            {
              icon: <PencilIcon size={20} color={colors.primary} />,
              title: 'Edit',
              onClick: () => setIsEditing((prev) => !prev),
              disabled: isLoading,
            }
          ]),
      ]}
      content={
        isEditing ? (
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={options}
              value={selected}
              disabled={isLoading}
              onChange={(_event, value) => setSelected(value)}
              filterOptions={filterBrands}
              getOptionLabel={(brand: BrandOption) =>
                `${[brand.nameEn, brand.nameAr].filter(Boolean).join(' / ')} (${brand.productCount})`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Brand" placeholder="Clear for no brand" />
              )}
            />
          </Grid>
        ) : (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <ProductInfoItem label={'Brand (EN)'} value={product.brand?.nameEn ?? null} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <ProductInfoItem label={'Brand (AR)'} value={product.brand?.nameAr ?? null} />
            </Grid>
          </>
        )
      }
    />
  )
}

export default memo(BrandDetails)
